import React, { useState, useEffect } from 'react';
import { X, Upload, Instagram, Music2, Camera, Video } from 'lucide-react';
import StarRating from './StarRating';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewModal = ({ onClose, onSubmit, editReview = null }) => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    author: editReview?.author || user?.name || '',
    rating: editReview?.rating || 0,
    text: editReview?.text || '',
    instagram: editReview?.instagram || '',
    tiktok: editReview?.tiktok || ''
  });
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(editReview?.avatar_url || null);
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(editReview?.image_url || editReview?.video_url || null);
  const [attachmentType, setAttachmentType] = useState(editReview?.video_url ? 'video' : 'image');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.name && !editReview) {
      setFormData(prev => ({ ...prev, author: user.name }));
    }
  }, [user, editReview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size must be less than 5MB');
        return;
      }
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(isVideo ? 'Video size must be less than 50MB' : 'Image size must be less than 10MB');
        return;
      }
      setAttachment(file);
      setAttachmentType(isVideo ? 'video' : 'image');
      setAttachmentPreview(URL.createObjectURL(file));
    }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: fd });
    if (res.ok) {
      const data = await res.json();
      return data.url;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) { alert('Please login to leave a review'); return; }
    if (formData.rating === 0) { alert('Please select a star rating'); return; }
    if (!formData.text.trim()) { alert('Please write your review'); return; }

    setUploading(true);
    
    try {
      let avatarUrl = editReview?.avatar_url || null;
      let imageUrl = editReview?.image_url || null;
      let videoUrl = editReview?.video_url || null;
      
      // Upload avatar if selected
      if (avatar) {
        avatarUrl = await uploadFile(avatar);
      }

      // Upload attachment if selected
      if (attachment) {
        const url = await uploadFile(attachment);
        if (attachmentType === 'video') {
          videoUrl = url;
          imageUrl = null;
        } else {
          imageUrl = url;
          videoUrl = null;
        }
      }

      onSubmit({
        ...formData,
        author: user?.name || formData.author,
        id: editReview?.id || null,
        avatar_url: avatarUrl,
        image_url: imageUrl,
        video_url: videoUrl
      });
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content review-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title-section">
              <h2 className="modal-title">{t('login')}</h2>
              <p className="modal-subtitle">{t('signInAccess')}</p>
            </div>
            <button className="modal-close" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          
          <div className="login-prompt">
            <p>{t('signInAccess')}</p>
            <div className="login-prompt-buttons">
              <button className="btn-primary" onClick={() => window.location.href = '/login'}>
                {t('login')}
              </button>
              <button className="btn-secondary" onClick={() => window.location.href = '/register'}>
                {t('createAccount')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-title">{editReview ? t('edit') + ' ' + t('review') : t('leaveReview')}</h2>
            <p className="modal-subtitle">
              {editReview ? t('edit') : t('writeReview')}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="review-form">
          <div className="form-group">
            <label htmlFor="author">{t('fullName')}</label>
            <input type="text" id="author" name="author" value={formData.author} disabled className="input-disabled" />
          </div>

          <div className="form-group">
            <label>{t('rating')} *</label>
            <div className="rating-selector">
              <StarRating rating={formData.rating} onRatingChange={handleRatingChange} />
              {formData.rating > 0 && (
                <span className="rating-text">{formData.rating} {formData.rating === 1 ? t('star') : t('stars')}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="text">{t('review')} *</label>
            <textarea id="text" name="text" value={formData.text} onChange={handleChange} placeholder={t('writeReview')} rows="4" required />
          </div>

          {/* Avatar Upload */}
          <div className="form-group">
            <label><Camera size={15} style={{ display: 'inline', marginRight: '5px', verticalAlign: '-2px' }} />Your Photo ({t('optional') || 'Optional'})</label>
            <div className="photo-upload-section">
              {avatarPreview ? (
                <div className="avatar-preview-wrap" data-testid="avatar-preview">
                  <img src={avatarPreview} alt="Avatar" className="avatar-preview-img" />
                  <button type="button" className="btn-remove-photo" onClick={() => { setAvatar(null); setAvatarPreview(null); }}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label className="photo-upload-btn avatar-upload-btn" data-testid="avatar-upload-btn">
                  <Camera size={18} />
                  <span>Upload Photo</span>
                  <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
                </label>
              )}
            </div>
          </div>

          {/* Attachment Upload (Image or Video) */}
          <div className="form-group">
            <label><Upload size={15} style={{ display: 'inline', marginRight: '5px', verticalAlign: '-2px' }} />Share Experience ({t('optional') || 'Optional'})</label>
            <div className="photo-upload-section">
              {attachmentPreview ? (
                <div className="attachment-preview-wrap" data-testid="attachment-preview">
                  {attachmentType === 'video' ? (
                    <video src={attachmentPreview} className="attachment-preview-media" controls />
                  ) : (
                    <img src={attachmentPreview} alt="Attachment" className="attachment-preview-media" />
                  )}
                  <button type="button" className="btn-remove-photo" onClick={() => { setAttachment(null); setAttachmentPreview(null); }}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="attachment-upload-row">
                  <label className="photo-upload-btn" data-testid="image-upload-btn">
                    <Upload size={18} />
                    <span>Image</span>
                    <input type="file" accept="image/*" onChange={handleAttachmentChange} style={{ display: 'none' }} />
                  </label>
                  <label className="photo-upload-btn" data-testid="video-upload-btn">
                    <Video size={18} />
                    <span>Video</span>
                    <input type="file" accept="video/*" onChange={handleAttachmentChange} style={{ display: 'none' }} />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Social Media Links */}
          <div className="form-group">
            <label>
              <Instagram size={16} style={{ display: 'inline', marginRight: '6px' }} />
              Instagram ({t('optional') || 'Optional'})
            </label>
            <input type="text" name="instagram" value={formData.instagram} onChange={handleChange} placeholder="@username" />
          </div>

          <div className="form-group">
            <label>
              <Music2 size={16} style={{ display: 'inline', marginRight: '6px' }} />
              TikTok ({t('optional') || 'Optional'})
            </label>
            <input type="text" name="tiktok" value={formData.tiktok} onChange={handleChange} placeholder="@username" />
          </div>

          <button type="submit" className="btn-primary btn-submit-review" disabled={uploading} data-testid="submit-review-btn">
            {uploading ? (t('uploading') || 'Uploading...') : (editReview ? t('saveChanges') : t('submitReview'))}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;
